<script setup lang="ts">import { ref as _ref, toRef as _toRef } from 'vue';

import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'

import { useWindowScroll, whenever } from '@vueuse/core'
import { useScreen } from '@consumer/helpers/screen'

import type { CurrentUser } from '@consumer/types/serializers'

import type GButton from '@consumer/components/GButton.vue'
import CartMenu from './Header/CartMenu.vue'
import CategoryBar from './Header/CategoryBar.vue'
import FavoritesMenu from './Header/FavoritesMenu.vue'
import GiftlyLogo from './Header/GiftlyLogo.vue'
import MainContainer from './Header/MainContainer.vue'
import UserMenu from './Header/UserMenu.vue'
import Overlay, { searchBarFocused } from './Overlay.vue'
import OmniSearch from './Header/OmniSearch.vue'

const mobileSearchButton = _ref<InstanceType<typeof GButton>>()

defineOptions({
  inheritAttrs: false,
})

defineEmits(['close'])

withDefaults(defineProps<{
  currentUser?: CurrentUser | null
}>(), {  })

let showCategoryBar = _ref(false)

const __$temp_1 = (useScreen()),
  isDesktop = _toRef(__$temp_1, 'isDesktop');
whenever(() => isDesktop.value && searchBarFocused.value, () => {
  showCategoryBar.value = true
})

let showMobileSearchBar = _ref(false)
const toggleMobileSearch = () => {
  showMobileSearchBar.value = !showMobileSearchBar.value
  showCategoryBar.value = showMobileSearchBar.value
  if (!showMobileSearchBar.value) {
    mobileSearchButton.value?.$el?.blur()
  }
}

const __$temp_2 = (useWindowScroll()),
  y = _toRef(__$temp_2, 'y');
const scrolled = computed(() => y.value > 150)
</script>

<template>
  <div
    :class="[
      'lg:fixed top-0 w-full z-navbar bg-white',
      scrolled ? 'lg:shadow-v2md' : null
    ]"
    @click="showCategoryBar = true"
  >
    <V2Container class="!p-0 w-full">
      <MainContainer :withSearchBar="showCategoryBar">
        <div class="w-full flex items-center justify-between h-15">
          <GiftlyLogo/>

          <div class="hidden lg:block flex-grow px-15">
            <OmniSearch/>
          </div>

          <div class="flex items-center h-full gap-2">
            <GButton
              ref="mobileSearchButton"
              variant="secondary"
              size="small"
              class="toggle-search !lg:hidden !shadow-none"
              :class="showMobileSearchBar && '!text-v2-active !bg-surface-blue'"
              style="height: 44px; padding: 0.75rem; width: 44px;"
              data-track-ga="mobile_nav_search_icon_tap"
              :icon="faMagnifyingGlass"
              iconSize="md"
              aria-label="Search"
              @click="toggleMobileSearch"
            />

            <div class="flex items-center gap-2 lg:gap-4" @click.stop>
              <FavoritesMenu/>
              <CartMenu/>
              <UserMenu :currentUser="currentUser"/>
            </div>
          </div>
        </div>
      </MainContainer>
      <Transition name="slide-down">
        <div v-show="showMobileSearchBar" class="lg:hidden">
          <OmniSearch/>
        </div>
      </Transition>
    </V2Container>
  </div>

  <div class="lg:h-[84px]"/>

  <Transition name="slide-down">
    <CategoryBar v-show="showCategoryBar"/>
  </Transition>

  <Overlay/>
</template>

<style lang="scss" scoped>
  // Fix mobile styles to not show the hover state after the user taps the button
  @media screen and (max-width: 768px) {
    .toggle-search:hover {
      --bg: white !important;
      --br: theme("colors.grey.primary");
      --bg-focus: theme("colors.primary.pale");
      --color: theme("colors.v2-tertiary");
      --color-focus: theme("colors.v2-tertiary");
    }
  }
</style>
